/**
 * OAuth2 Consent Page
 *
 * Served at /oauth/consent when a third-party app initiates an Authorization
 * Code + PKCE flow against this Supabase project's OAuth2 server.
 *
 * Flow:
 *  1. Partner app redirects user to Supabase /auth/v1/oauth/authorize
 *  2. Supabase validates the request, then redirects to this page with ?authorization_id=<id>
 *  3. If the user isn't signed in, we redirect to /login?return=/oauth/consent?authorization_id=<id>
 *  4. Once signed in, we fetch authorization details and show Allow / Deny
 *  5. The consent decision is posted back to Supabase; it redirects user to partner redirect_uri
 */

import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "../services/supabase";
import { useAuth } from "../contexts/AuthContext";
import Button from "../components/common/Button";

interface AuthDetails {
  client: {
    client_id: string;
    client_name: string;
    client_uri?: string;
    logo_uri?: string;
  };
  user: { id: string; email: string };
  scope: string;
  redirect_uri?: string;
}

const OAuthConsent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const authorizationId = searchParams.get("authorization_id");

  const [details, setDetails] = useState<AuthDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect to login if not authenticated, preserving this URL via sessionStorage
  useEffect(() => {
    if (!isAuthenticated) {
      sessionStorage.setItem(
        "returnTo",
        window.location.pathname + window.location.search,
      );
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Fetch authorization details once authenticated
  useEffect(() => {
    if (!isAuthenticated || !authorizationId) {
      return;
    }

    (async () => {
      setLoading(true);
      const { data, error: err } =
        await supabase.auth.oauth.getAuthorizationDetails(authorizationId);

      if (err) {
        setError(err.message ?? "Could not load authorization request.");
        setLoading(false);
        return;
      }

      // If already consented (redirect_uri present), just follow it
      if (data?.redirect_uri) {
        window.location.href = data.redirect_uri;
        return;
      }

      setDetails(data as AuthDetails);
      setLoading(false);
    })();
  }, [isAuthenticated, authorizationId]);

  const handleApprove = async () => {
    if (!authorizationId) {
      return;
    }
    setDeciding(true);
    const { data, error: err } = await supabase.auth.oauth.approveAuthorization(
      authorizationId,
      {
        skipBrowserRedirect: true,
      },
    );
    if (err || !data?.redirect_url) {
      setError(err?.message ?? "Authorization failed.");
      setDeciding(false);
      return;
    }
    window.location.href = data.redirect_url;
  };

  const handleDeny = async () => {
    if (!authorizationId) {
      return;
    }
    setDeciding(true);
    const { data, error: err } = await supabase.auth.oauth.denyAuthorization(
      authorizationId,
      {
        skipBrowserRedirect: true,
      },
    );
    if (err || !data?.redirect_url) {
      setError(err?.message ?? "Could not cancel authorization.");
      setDeciding(false);
      return;
    }
    window.location.href = data.redirect_url;
  };

  // --- Render states ---

  if (!isAuthenticated) {
    return null;
  } // redirect in progress

  if (!authorizationId) {
    return (
      <PageShell>
        <p className="text-red-600 text-sm">
          Missing authorization ID. Please retry from the app.
        </p>
      </PageShell>
    );
  }

  if (loading) {
    return (
      <PageShell>
        <p className="text-gray-500 text-sm">Loading authorization request…</p>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <p className="text-red-600 text-sm">{error}</p>
      </PageShell>
    );
  }

  if (!details) {
    return null;
  }

  const scopes = details.scope ? details.scope.split(" ").filter(Boolean) : [];
  const scopeLabels: Record<string, string> = {
    openid: "Confirm your identity",
    email: "Read your email address",
    profile: "Read your profile name and picture",
    phone: "Read your phone number",
  };

  return (
    <PageShell>
      {/* App identity */}
      <div className="flex items-center gap-4 mb-6">
        {details.client.logo_uri && (
          <img
            src={details.client.logo_uri}
            alt={details.client.client_name}
            className="h-12 w-12 rounded-lg object-contain border border-gray-200"
          />
        )}
        <div>
          <p className="font-semibold text-gray-900">
            {details.client.client_name}
          </p>
          {details.client.client_uri && (
            <a
              href={details.client.client_uri}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-600 hover:underline"
            >
              {details.client.client_uri}
            </a>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-700 mb-4">
        <strong>{details.client.client_name}</strong> is requesting access to
        your U.S. LawShield account{user?.email ? ` (${user.email})` : ""}.
      </p>

      {scopes.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            This app will be able to:
          </p>
          <ul className="space-y-1">
            {scopes.map((s) => (
              <li
                key={s}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <svg
                  className="h-4 w-4 text-green-500 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {scopeLabels[s] ?? s}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-3">
        <Button
          variant="primary"
          onClick={handleApprove}
          disabled={deciding}
          className="flex-1"
        >
          {deciding ? "…" : "Allow"}
        </Button>
        <Button
          variant="secondary"
          onClick={handleDeny}
          disabled={deciding}
          className="flex-1"
        >
          Deny
        </Button>
      </div>

      <p className="mt-4 text-xs text-gray-400 text-center">
        You are signed in as {details.user.email}. This will not share your
        password.
      </p>
    </PageShell>
  );
};

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
    <div className="max-w-md w-full">
      <div className="text-center mb-8">
        <img
          src="/logowhite.png"
          alt="U.S. LawShield"
          className="mx-auto h-20 w-auto mb-4"
        />
        <h1 className="text-xl font-semibold text-gray-900">
          Authorize Application
        </h1>
      </div>
      <div className="bg-white shadow-lg rounded-xl p-8">{children}</div>
    </div>
  </div>
);

export default OAuthConsent;
